import {
  type CaraWorkspaceFieldsFragment,
  CaraWorkspacePlanStageEventType,
  CaraWorkspaceTool
} from '@oysterjs/ais/api/graphql';
import { isBulkEmailDraftCreate } from '@oysterjs/ais/components/workspaces/lib/bulk-email-draft-event';

type StageEvent = CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];

/**
 * Classifies how a stage event participates in the conversation timeline.
 *
 * - `row`: collapsible row inside a `StageEventGroup` (thoughts, searches, integration writes,
 *   PDF list/inspect, etc.).
 * - `artifact`: standalone artifact card that breaks out of the surrounding row group
 *   (drafts, file edits, PDF fills, application files, etc.).
 * - `hidden`: rendered as nothing and omitted from any group's step count.
 */
export type StageEventKind = 'row' | 'artifact' | 'hidden';

/** What the chat knows of its bulk drafts, to show each one card per turn and none for its emails. */
export type BulkEmailDraftCards = {
  /** Bulk draft events whose card a later event of the same turn shows. */
  supersededEventIds: ReadonlySet<string>;
  /** The emails drafted for a bulk draft, which show on its card and page instead. */
  emailArtifactIds: ReadonlySet<string>;
};

/**
 * Returns the static (state-independent where possible) classification for a stage event.
 *
 * `nextEvent` is the event immediately after `event` in the same plan stage, used only by
 * `FILE_EDITOR`, which can emit multiple consecutive events for the same file and only
 * shows an artifact card on the final one.
 *
 * Keep in sync with `conversation-stage-event-renderers.tsx`.
 */
export const getStageEventKind = (
  event: StageEvent,
  nextEvent?: StageEvent | undefined,
  bulkEmailDraftCards?: BulkEmailDraftCards
): StageEventKind => {
  if (event.type === CaraWorkspacePlanStageEventType.Thought) return 'row';

  switch (event.toolCall?.type) {
    case CaraWorkspaceTool.SearchFile:
    case CaraWorkspaceTool.SearchIntegration:
    case CaraWorkspaceTool.SearchKnowledgeBase:
    case CaraWorkspaceTool.SearchKnowledgeGraph:
    case CaraWorkspaceTool.SearchWeb:
    case CaraWorkspaceTool.WriteIntegration:
    case CaraWorkspaceTool.AgencyzoomWrite:
    case CaraWorkspaceTool.HawksoftWrite:
    case CaraWorkspaceTool.NowcertsWrite:
    case CaraWorkspaceTool.EzlynxWrite:
    case CaraWorkspaceTool.Ams360Write:
    case CaraWorkspaceTool.QqcatalystWrite:
    case CaraWorkspaceTool.CodeExecution:
      return 'row';

    case CaraWorkspaceTool.DraftOutboundCall:
    case CaraWorkspaceTool.DraftDocument:
    case CaraWorkspaceTool.FillTemplate:
      return event.error ? 'hidden' : 'artifact';

    case CaraWorkspaceTool.BulkEmailDraft:
      // Without them bulk send is off and the card renders nothing.
      if (!bulkEmailDraftCards || bulkEmailDraftCards.supersededEventIds.has(event.id)) {
        return 'hidden';
      }
      // A failed CREATE leaves no bulk draft to open, so its card says so.
      return event.error && !isBulkEmailDraftCreate(event) ? 'hidden' : 'artifact';

    case CaraWorkspaceTool.DraftEmail: {
      if (event.error) return 'hidden';
      const artifactId = event.toolCall.draftEmailTool?.output?.artifactId;
      if (artifactId && bulkEmailDraftCards?.emailArtifactIds.has(artifactId)) return 'hidden';
      // CREATE_DRAFT, CREATE_FROM_TEMPLATE and UPDATE_DRAFT each produce a card
      // for the version they wrote. A mailbox copy changes nothing in the
      // workspace draft the reader already has. An event with no operation was
      // written before there were any, and only composing was recorded under
      // this type then.
      const op = event.toolCall.draftEmailTool?.input?.operation;
      const writesVersion =
        !op || op === 'CREATE_DRAFT' || op === 'CREATE_FROM_TEMPLATE' || op === 'UPDATE_DRAFT';
      return writesVersion ? 'artifact' : 'hidden';
    }

    case CaraWorkspaceTool.ApplicationFileGenerator:
    case CaraWorkspaceTool.DocxEditor:
    case CaraWorkspaceTool.XlsxEditor:
      return event.error ? 'hidden' : 'artifact';

    case CaraWorkspaceTool.FileEditor: {
      if (event.error) return 'hidden';
      const op = event.toolCall.fileEditorTool?.input?.operation;
      if (!op || op === 'READ') return 'hidden';
      // Multiple consecutive FILE_EDITOR events for the same file collapse to a single
      // card on the final event; intermediate ones render nothing.
      if (
        nextEvent?.toolCall?.type === CaraWorkspaceTool.FileEditor &&
        nextEvent.toolCall.fileEditorTool?.output?.fileId ===
          event.toolCall.fileEditorTool?.output?.fileId
      ) {
        return 'hidden';
      }
      return 'artifact';
    }

    case CaraWorkspaceTool.PdfEditor: {
      const op = event.toolCall.pdfEditorTool?.input?.operation;
      if (op === 'LIST' || op === 'DESCRIBE' || op === 'INSPECT_FORM_FIELDS') return 'row';
      if (op === 'FILL') return event.error ? 'row' : 'artifact';
      if (op === 'FILL_FORM_FIELDS') return event.error ? 'hidden' : 'artifact';
      return 'hidden';
    }

    default:
      return 'hidden';
  }
};
